import { useCallback } from "react";
import moment from "moment";

// =======================================================
// useCalendar
// -------------------------------------------------------
// Provides reusable calendar data transformations.
//
// Responsibilities:
// - Group available days by month
// - Group artists inside each month
// - Sort calendar months chronologically
//
// State and UI logic remain inside Home.js.
// This hook contains no API calls.
// =======================================================

const useCalendar = () => {
    // =======================================================
    // Group Available Days
    // -------------------------------------------------------
    // Converts the flat available-days array into the calendar
    // structure used by Home.js.
    // =======================================================

    const groupAvailableDays = useCallback((availableDays = []) => {
        const grouped = {};

        availableDays.forEach((day) => {
            const monthKey = moment(day.day).format("YYYY-MM");
            const monthLabel = moment(day.day).format("MMMM YYYY");

            if (!grouped[monthKey]) {
                grouped[monthKey] = {
                    label: monthLabel,
                    artists: {},
                };
            }

            if (!grouped[monthKey].artists[day.artistName]) {
                grouped[monthKey].artists[day.artistName] = {
                    artistName: day.artistName,
                    dates: [],
                };
            }

            grouped[monthKey].artists[day.artistName].dates.push({
                ...day,
                appointments: day.appointments || [],
                bookingStatus: day.bookingStatus,
            });
        });

        return Object.keys(grouped)
            .sort()
            .reduce((sorted, monthKey) => {
                sorted[monthKey] = grouped[monthKey];
                return sorted;
            }, {});
    }, []);

    // =======================================================
    // Filter Grouped Days
    // -------------------------------------------------------
    // Filters grouped calendar data by artist and client name.
    // Keeps the original grouped structure for rendering.
    // =======================================================

    const filterGroupedDays = useCallback(
        (groupedDays = {}, artistSearch = "", clientSearch = "") => {
            if (!artistSearch && !clientSearch) {
                return groupedDays;
            }

            const filtered = {};

            Object.entries(groupedDays).forEach(([monthKey, monthData]) => {
                const matchingArtists = {};

                Object.entries(monthData.artists || {}).forEach(
                    ([artistName, artistInfo]) => {
                        const artistMatches = artistSearch
                            ? artistName
                                .toLowerCase()
                                .includes(artistSearch.toLowerCase())
                            : true;

                        if (!artistMatches) return;

                        const matchingDates = (artistInfo.dates || [])
                            .map((day) => {
                                const matchingAppointments = (
                                    day.appointments || []
                                ).filter(
                                    (appointment) =>
                                        !clientSearch ||
                                        (appointment.clientName || "")
                                            .toLowerCase()
                                            .includes(clientSearch.toLowerCase())
                                );

                                return {
                                    ...day,
                                    appointments: matchingAppointments,
                                };
                            })
                            .filter(
                                (day) => (day.appointments || []).length > 0
                            );

                        if (matchingDates.length > 0) {
                            matchingArtists[artistName] = {
                                ...artistInfo,
                                dates: matchingDates,
                            };
                        }
                    }
                );

                if (Object.keys(matchingArtists).length > 0) {
                    filtered[monthKey] = {
                        ...monthData,
                        artists: matchingArtists,
                    };
                }
            });

            return filtered;
        },
        []
    );

    // =======================================================
    // Build Mobile Sidebar
    // -------------------------------------------------------
    // Builds the artist/day structure for the mobile sidebar.
    // =======================================================

    const buildMobileSidebar = useCallback(
        (filteredGroupedDays = {}, sidebarMonth) => {
            const artists = {};

            Object.values(filteredGroupedDays).forEach((monthData) => {
                Object.values(monthData.artists || {}).forEach(
                    (artistInfo) => {
                        const artistName = artistInfo.artistName;

                        artistInfo.dates.forEach((day) => {
                            if (
                                moment(day.day).format("YYYY-MM") !== sidebarMonth
                            ) {
                                return;
                            }

                            if (!artists[artistName]) {
                                artists[artistName] = [];
                            }

                            artists[artistName].push({
                                // calendar-profile-artist-id: never match profile images by name.
                                artistId: day.artistId,
                                dayId: day.dayId,
                                day: day.day,
                                bookingStatus: day.bookingStatus,
                            });
                        });
                    }
                );
            });

            Object.keys(artists).forEach((artistName) => {
                artists[artistName].sort((a, b) =>
                    moment(a.day).diff(moment(b.day))
                );
            });

            return artists;
        },
        []
    );

    return {
        groupAvailableDays,
        filterGroupedDays,
        buildMobileSidebar,
    };
};

export default useCalendar;